import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Tabs, Tab, Box, Button, Tooltip } from '@mui/material';
//Components
import MarkerInformationTab from './MarkerInformationTab';
import MarkerConnectorTab from './MarkerConnectorTab';
import CustomerReview from '../customer-review';
import Photos from '../photos';
import Constant from '../../util/constants';
import { Context } from '../../context';

import { Icon } from '@iconify/react';
import { styled, alpha } from '@mui/material/styles';

const StyledTabs = styled(Tabs)(({ theme }) => ({
  width: '100%',
  maxWidth: '100%',
  borderRadius: 0,
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
  padding: theme.spacing(0.75),
  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.05)}`,
  border: `1px solid ${alpha(theme.palette.primary.light, 0.2)}`,
  overflow: 'hidden',
  boxSizing: 'border-box',
  position: 'relative',
  '& .MuiTabs-flexContainer': {
    justifyContent: 'space-between',
    width: '100%',
    maxWidth: '100%',
    overflow: 'hidden',
    flexWrap: 'nowrap',
    gap: theme.spacing(0.5),
  },
  '& .MuiTabs-indicator': {
    display: 'none',
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  color: theme.palette.text.secondary,
  backgroundColor: 'transparent',
  borderRadius: 0,
  fontWeight: 700,
  fontSize: '0.875rem',
  letterSpacing: '0.5px',
  textTransform: 'none',
  minHeight: 48,
  flex: '1 1 auto',
  minWidth: 0,
  maxWidth: 'none',
  margin: theme.spacing(0.25),
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  boxSizing: 'border-box',
  lineHeight: 1.2,
  textAlign: 'center',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
    transition: 'left 0.5s ease-in-out',
  },
  '&:hover': {
    backgroundColor: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
    color: theme.palette.primary.main,
    transform: 'translateY(-1px)',
    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
    '&:before': {
      left: '100%',
    },
  },
  '&.Mui-selected': {
    color: 'white',
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.4)}, 0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}`,
    transform: 'translateY(-2px)',
    '&:hover': {
      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
      boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.5)}, 0 0 0 1px ${alpha(theme.palette.primary.main, 0.3)}`,
      transform: 'translateY(-3px)',
    },
    '&:before': {
      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
    },
  },
  '&:active': {
    transform: 'translateY(0)',
  },
  // Responsive behavior
  '@media (max-width: 768px)': {
    width: '100%',
    maxWidth: '100%',
    minWidth: 'unset',
  },
  '@media (max-width: 1200px)': {
    minWidth: '100px',
    maxWidth: '150px',
    fontSize: '0.8rem',
  },
}));

const styles = {
  tabParent: {
    width: '100%',
    // bgcolor: '#f3f3f3',
    borderRadius: '15px', 
    // padding: '15px',
    marginBottom: '14px',
    // '& .MuiTabs-flexContainer': {
    //   width: 'max-content'
    // }
  },
  reportButton: {
    '&:hover': {}
  }
}

const TabPanel = React.memo((props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
});

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

function LocationTabs({ locationDetail, onReportClick, networkFiltersArr, isThirdPartyCPO, onClose, refreshLocationDetail }) {
  const [value, setValue] = React.useState(0);
  const { auth, user } = useContext(Context);

  const handleChange = (event, newValue) => {
    const { authenticated, setAuthenticated } = auth;
    if([2, 3].includes(newValue) && !authenticated.isAuth){
      setAuthenticated({ 
        ...authenticated, 
        authPopup: !0,
        redirectTo: Constant.paths.HOME,
      });
      return;
    }

    setValue(newValue);
  };

  return (<>
    <Box className='tabs-parent' sx={styles.tabParent}>
      <Box sx={{ py: 0.1, mb: 0.1, display: 'flex', alignItems: 'center' }}>
        <StyledTabs value={value} onChange={handleChange} variant="fullWidth" className='tabs' aria-label="Location details tab">
          <Tooltip title={Constant.label.STATIONS} placement="top">
            <StyledTab className="tab-connector" icon={<Icon icon="eva:charging-outline" width={24} height={24} />} {...a11yProps(0)} />
          </Tooltip>
          <Tooltip title={Constant.label.INFORMATION} placement="top">
            <StyledTab className="tab-information" icon={<Icon icon="eva:info-outline" width={24} height={24} />} {...a11yProps(1)} />
          </Tooltip>
          <Tooltip title={Constant.label.CUSTOMER_REVIEWS} placement="top">
            <StyledTab className="tab-reported-issue" icon={<Icon icon="eva:message-circle-outline" width={24} height={24} />} {...a11yProps(2)} />
          </Tooltip>
          <Tooltip title={Constant.label.PHOTOS} placement="top">
            <StyledTab className="tab-photos" icon={<Icon icon="eva:image-outline" width={24} height={24} />} {...a11yProps(3)} />
          </Tooltip>
        </StyledTabs>
      </Box>
      <TabPanel value={value} index={0} className="tab-list tab-location">
        <MarkerConnectorTab isThirdPartyCPO={isThirdPartyCPO} locationDetail={locationDetail} networkFiltersArr={networkFiltersArr} />
      </TabPanel>
      <TabPanel value={value} index={1} className="tab-list tab-info">
        <MarkerInformationTab locationDetail={locationDetail[0]} forLocationTab= {true} isThirdPartyCPO={isThirdPartyCPO} />
      </TabPanel>
      <TabPanel value={value} index={2} className="tab-list tab-customer-reviews">
        <CustomerReview user={user.userDetail} location={locationDetail[0]} />
      </TabPanel>
      <TabPanel value={value} index={3} className="tab-list tab-photos">
        <Photos location={locationDetail[0]} refreshLocationDetail={refreshLocationDetail} />
      </TabPanel>
    </Box>
    {![2, 3].includes(value) && 
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', pb: 2, gap: 1.5 }}>
      <Button 
        variant="outlined" 
        color="inherit"
        onClick={onClose} 
      >
        {Constant.label.CLOSE || 'Close'}
      </Button>
      <Button 
        variant="contained" 
        data-render-report="0" 
        onClick={onReportClick(!1, !0)} 
        sx={{ fontWeight: "bold" }}
        startIcon={<Icon icon="eva:alert-triangle-fill" width={20} />}
      >
        {Constant.label.REPORT_ISSUE}
      </Button>
    </Box>}
  </>);
}   

export default LocationTabs;